import React, { useState } from 'react';
import { useStripe, useElements, CardElement, AddressElement } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import Service from '../Dashboard/services/Service';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { getStripeErrorMessage, isRetryableError } from '../../../Shared/Utils/StripeErrorHandler';
import './Payment.scss';

const StripePaymentForm = ({ amount, medicationRequestId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [paymentError, setPaymentError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [billingInfo, setBillingInfo] = useState({
    name: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
    },
  });
  const [errors, setErrors] = useState({});
  const [clientSecret, setClientSecret] = useState(null);

  const validateAddress = (info) => {
    const validationErrors = {};
    if (!info.name) validationErrors.name = 'Name is required.';
    if (!info.address.line1) validationErrors.line1 = 'Address line 1 is required.';
    if (!info.address.city) validationErrors.city = 'City is required.';
    if (!info.address.postal_code) validationErrors.postal_code = 'Postal code is required.';
    if (!info.address.country) validationErrors.country = 'Country is required.';
    return validationErrors;
  };

  const handleAddressChange = (event) => {
    const value = event?.value ?? {};
    const newBillingInfo = {
      name: value.name ?? '',
      address: {
        line1: value.address?.line1 ?? '',
        line2: value.address?.line2 ?? '',
        city: value.address?.city ?? '',
        state: value.address?.state ?? '',
        postal_code: value.address?.postal_code ?? '',
        country: value.address?.country ?? 'US',
      },
    };
    setBillingInfo(newBillingInfo);
    setErrors(validateAddress(newBillingInfo));
  };

  const handleContinueToPayment = async () => {
    const validationErrors = validateAddress(billingInfo);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please complete the billing information.');
      return;
    }

    try {
      const payload = { amount: amount * 100 };
      if (medicationRequestId) {
        payload.medication_request_id = medicationRequestId;
      }
      const response = await Service.createPaymentIntent(payload);
      setClientSecret(response.data.client_secret);
      setCurrentStep(2);
    } catch (error) {
      toast.error('Failed to initialize payment. Please try again.');
      console.error('Payment intent creation error:', error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      toast.error('Payment system not ready. Please refresh the page.');
      return;
    }

    setLoading(true);
    setPaymentError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingInfo.name,
          address: {
            line1: billingInfo.address.line1,
            line2: billingInfo.address.line2,
            city: billingInfo.address.city,
            state: billingInfo.address.state,
            postal_code: billingInfo.address.postal_code,
            country: billingInfo.address.country,
          },
        },
      });

      if (error) {
        handlePaymentError(error);
        return;
      }

      const { error: confirmError } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id,
      });

      if (confirmError) {
        handlePaymentError(confirmError);
      } else {
        toast.success('Payment Successful!');
        setRetryCount(0);
        sessionStorage.removeItem('medicationRequestId');
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } catch (error) {
      console.error('Payment error:', error);
      handlePaymentError(error);
    }
  };

  const handlePaymentError = (error) => {
    const errorMessage = getStripeErrorMessage(error);
    setPaymentError(errorMessage);
    toast.error(errorMessage);

    if (isRetryableError(error) && retryCount < 2) {
      toast.info('You can retry the payment.');
      setRetryCount(retryCount + 1);
    }

    setLoading(false);
  };

  return (
    <div className="shopify-payment-form">
      {/* Step 1: Billing Information */}
      <div className={`checkout-section ${currentStep >= 1 ? 'active' : ''}`}>
        <div className="section-header">
          <div className="step-number">1</div>
          <h2>Billing Information</h2>
          {currentStep > 1 && (
            <button type="button" className="edit-button" onClick={() => setCurrentStep(1)}>
              Edit
            </button>
          )}
        </div>

        {currentStep === 1 && (
          <div className="section-content">
            <div className="address-element-container">
              <AddressElement
                options={{
                  mode: 'billing',
                  defaultValues: billingInfo,
                  fields: {
                    phone: 'never',
                  },
                }}
                onChange={handleAddressChange}
              />
              {Object.keys(errors).length > 0 && (
                <div className="error-messages">
                  {Object.values(errors).map((error, i) => (
                    <p key={i} className="error-message">
                      {error}
                    </p>
                  ))}
                </div>
              )}
            </div>
            <button type="button" className="continue-button" onClick={handleContinueToPayment}>
              Continue to Payment
            </button>
          </div>
        )}

        {currentStep > 1 && (
          <div className="section-summary">
            <p>{billingInfo.name}</p>
            <p>{billingInfo.address.line1}</p>
            <p>
              {billingInfo.address.city}, {billingInfo.address.state} {billingInfo.address.postal_code}
            </p>
          </div>
        )}
      </div>

      {/* Step 2: Payment Method */}
      <div className={`checkout-section ${currentStep >= 2 ? 'active' : 'disabled'}`}>
        <div className="section-header">
          <div className="step-number">2</div>
          <h2>Payment Method</h2>
        </div>

        {currentStep === 2 && clientSecret && (
          <div className="section-content">
            <form onSubmit={handleSubmit}>
              <div className="payment-element-container">
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                    },
                  }}
                />
              </div>

              {paymentError && (
                <div className="error-alert">
                  <strong>Payment Error:</strong> {paymentError}
                </div>
              )}

              {retryCount > 0 && <div className="info-alert">Retry attempt {retryCount} of 2</div>}

              <button type="submit" disabled={!stripe || loading} className="pay-button">
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Processing...
                  </>
                ) : (
                  `Complete Order • $${amount}`
                )}
              </button>

              <div className="payment-security">
                <p>🔒 Your payment information is secure and encrypted</p>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

StripePaymentForm.propTypes = {
  amount: PropTypes.number.isRequired,
  medicationRequestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default StripePaymentForm;
